import { useEffect, useRef, useState } from 'react';
import { api, type Asset, type Card, type Quote, type Source, type Turn } from '../../lib/api';
import { accepts, isAssetDrag, readDrag, writeDrag, type DragAsset } from '../../lib/drag';
import { footageOf, sceneElapsed, tick } from '../../lib/playback';
import { mmss, sceneAt, startOffsets } from '../../lib/timeline';
import { GIVE_UP_AFTER, LOOK_EVERY_MS, GAVE_UP, saySeconds } from '../../lib/waiting';
import { sayQuote } from '../../lib/quick';
import { FramePreview } from '../FramePreview';
import { Thumb, displayName } from '../AssetGrid';
import { OpenInPro, QuoteLine, SendButton, Stage } from './parts';
import type { TabProps } from './Quick';

// The Movie tab: clips in a row, a bed under, a cut out.
//
// A movie here is a card with scenes that each cut to footage and say
// nothing — the inserted-scene shape the Flight Deck already renders. The
// strip is its own simple thing, not the Pro timeline: drag to reorder,
// drop a picture or a clip from the library, set how long each holds, pick
// a bed. Rough play draws each scene with the same frame the renderer
// draws and runs the footage under it on the shared clock; the bed and the
// joins are heard in the render, and the stage says so. The render is one
// price because the renderer is one resolution, and the quote is the
// server's for the movie's length.

const STILL_SECONDS = 5;

interface Live { rundown: Turn[]; card: Card }

export function QuickMovie({ brand, cards, onCardMade, pass, onUsedPass, onOpenInPro, onGo, onError }: TabProps) {
  const [cardId, setCardId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [live, setLive] = useState<Live | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [renderStatus, setRenderStatus] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const [cut, setCut] = useState<string | null>(null);
  const [showCut, setShowCut] = useState(false);
  const media = useRef(new Map<string, string>());
  const [mediaReady, setMediaReady] = useState(0);
  const brandId = brand?.id ?? null;

  const mine = cards.filter((c) => !brandId || c.brand_id === brandId || c.brand_id === null);

  useEffect(() => {
    let alive = true;
    api.assets(brandId).then((all) => { if (alive) setAssets(all.filter((a) => a.status === 'ready')); }).catch(() => undefined);
    api.sources().then((r) => { if (alive) setSources(r.sources); }).catch(() => undefined);
    return () => { alive = false; };
  }, [brandId]);

  async function open(id: string) {
    try {
      const d = await api.card(id);
      setLive({ rundown: d.rundown, card: d.card }); setDirty(false); setSelected(d.rundown.length ? 0 : null); setPlayhead(null); setPlaying(false);
      setCut(null); setShowCut(false);
      setRenderStatus(d.job?.status === 'queued' || d.job?.status === 'running' ? d.job.status : null);
    } catch (e) { onError((e as Error).message); }
  }
  useEffect(() => { if (cardId) void open(cardId); else setLive(null); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [cardId]);

  async function makeCard() {
    const t = title.trim() || 'Untitled movie';
    setBusy('card'); onError(null);
    try { const c = await api.createCard({ title: t, brandId }); onCardMade(c); setTitle(''); setCardId(c.id); }
    catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  }

  /** A scene that cuts to this asset and says nothing. A source is made once per asset and reused. */
  async function turnFor(a: Pick<DragAsset, 'id' | 'kind' | 'name' | 'seconds'>): Promise<Turn> {
    let src = sources.find((s) => s.asset_id === a.id);
    if (!src) {
      src = await api.sourceFromAsset(a.id, a.name, a.kind === 'image' ? 'file' : 'tape');
      const all = await api.sources();
      setSources(all.sources);
    }
    const seconds = a.kind === 'image' ? STILL_SECONDS : Math.max(1, Math.round(a.seconds ?? (src.duration_ms ? src.duration_ms / 1000 : STILL_SECONDS)));
    // A clip keeps its own sound under the bed; a still has none to keep. The
    // default is 'over' (the footage muted under narration), which is right
    // for a Pro line with a spoken text and wrong for a Quick movie, where
    // nobody speaks and a Veo shot's explosions were the point of buying it.
    return { speaker: live?.rundown[0]?.speaker ?? 'Host', text: '', emotion: 'neutral', gesture: 'none', shot: 'wide', beat: 0, holdSeconds: seconds, sourceId: src.id, audio: a.kind === 'video' ? 'sync' : 'over' };
  }

  async function add(a: Pick<DragAsset, 'id' | 'kind' | 'name' | 'seconds'>, at: number | null = null) {
    if (!live) { onError('Pick a movie or make one first.'); return; }
    if (!accepts('broll', a).ok) { onError(accepts('broll', a).ok ? null : (accepts('broll', a) as { why: string }).why); return; }
    setBusy('add'); onError(null);
    try {
      const t = await turnFor(a);
      setLive((l) => {
        if (!l) return l;
        const rundown = [...l.rundown];
        rundown.splice(at ?? rundown.length, 0, t);
        return { ...l, rundown };
      });
      setDirty(true); setSelected(at ?? live.rundown.length);
    } catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  }

  // A clip handed from the Library goes on the end, once.
  useEffect(() => {
    if (!pass || pass.tab !== 'movie') return;
    onUsedPass();
    const a = assets.find((x) => x.id === pass.assetId);
    if (a && live) void add({ id: a.id, kind: a.kind, name: displayName(a), seconds: a.duration_ms ? a.duration_ms / 1000 : null });
    else if (a) onError(`Pick a movie or make one first, then add ${displayName(a)} from the strip below.`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass, assets.length, live?.card.id]);

  function move(from: number, to: number) {
    if (!live || from === to) return;
    const rundown = [...live.rundown];
    const [m] = rundown.splice(from, 1);
    if (!m) return;
    rundown.splice(to, 0, m);
    setLive({ ...live, rundown }); setDirty(true); setSelected(to);
  }
  function drop(i: number) {
    if (!live) return;
    setLive({ ...live, rundown: live.rundown.filter((_, j) => j !== i) }); setDirty(true);
    setSelected((s) => (s === null ? null : s >= live.rundown.length - 1 ? Math.max(0, live.rundown.length - 2) : s));
  }
  function hold(i: number, seconds: number) {
    if (!live || !(seconds > 0)) return;
    setLive({ ...live, rundown: live.rundown.map((t, j) => (j === i ? { ...t, holdSeconds: seconds } : t)) }); setDirty(true);
  }

  async function save() {
    if (!live || !cardId) return;
    setBusy('save'); onError(null);
    try { const r = await api.saveRundown(cardId, live.rundown); setLive({ rundown: r.rundown, card: r.card }); setDirty(false); }
    catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  }

  async function setBed(assetId: string) {
    if (!live || !cardId) return;
    setBusy('bed'); onError(null);
    try { const card = await api.setMix(cardId, assetId ? { assetId } : null); setLive({ ...live, card }); }
    catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  }

  // The clock: one rAF loop that owns real time; everything on screen is a function of the playhead.
  const secs = (live?.rundown ?? []).map((t) => (typeof t.holdSeconds === 'number' && t.holdSeconds > 0 ? t.holdSeconds : 1));
  const starts = startOffsets(secs);
  const total = secs.reduce((a, b) => a + b, 0);
  useEffect(() => {
    if (!playing) return;
    if (!(total > 0)) { setPlaying(false); return; }
    let raf = 0; let last = performance.now();
    const loop = (now: number) => {
      const dt = (now - last) / 1000; last = now;
      setPlayhead((was) => { const t = tick(was ?? 0, dt, total); if (t.done) setPlaying(false); return t.at; });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, total]);
  useEffect(() => {
    if (!playing || playhead === null) return;
    const at = sceneAt(starts, secs, playhead);
    setSelected((was) => (was === at ? was : at));
  }, [playing, playhead, starts, secs]);

  // The footage under the playhead, signed once and remembered; the next one fetched while this plays.
  const liveTurn = selected !== null ? live?.rundown[selected] ?? null : null;
  // Only footage is signed and run: a still is the frame the preview drew, and a JPEG in a <video> is a black box.
  const want = [footageOf(liveTurn, sources), selected !== null ? footageOf(live?.rundown[selected + 1], sources) : null].filter((x): x is string => Boolean(x));
  useEffect(() => {
    let alive = true;
    for (const id of want) {
      if (media.current.has(id)) continue;
      api.sourceMedia(id).then((r) => { if (alive && r.media?.url) { media.current.set(id, r.media.url); setMediaReady((n) => n + 1); } }).catch(() => undefined);
    }
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [want.join(',')]);
  void mediaReady;
  const liveFootage = footageOf(liveTurn, sources);
  const motionUrl = liveFootage ? media.current.get(liveFootage) ?? null : null;
  const motion = motionUrl && selected !== null && playhead !== null
    ? { url: motionUrl, at: sceneElapsed(starts, selected, playhead), playing, muted: true }
    : null;

  function togglePlay() {
    if (!live?.rundown.length) return;
    setPlaying((was) => { if (was) return false; setPlayhead((at) => (at === null || at >= total - 0.05 ? 0 : at)); return true; });
  }

  // The render's price for this length, from the server. Re-asked when the minute count changes, not on every held second.
  const minutes = Math.max(1, Math.ceil(total / 60));
  useEffect(() => {
    let alive = true;
    api.quote({ bench: 'render', inputs: { minutes } }).then((q) => { if (alive) setQuote(q); }).catch(() => undefined);
    return () => { alive = false; };
  }, [minutes]);

  const rendering = renderStatus === 'queued' || renderStatus === 'running';
  useEffect(() => {
    if (!rendering || !cardId) return;
    let alive = true; let looks = 0;
    setStalled(false);
    const t = setInterval(() => {
      if (looks++ >= GIVE_UP_AFTER) { clearInterval(t); setStalled(true); return; }
      api.card(cardId).then(async (d) => {
        if (!alive) return;
        const s = d.job?.status ?? null;
        setRenderStatus(s);
        if (s === 'complete') { clearInterval(t); try { setCut((await api.preview(cardId)).download.url); setShowCut(true); } catch { /* the Renders bench has it */ } }
        if (s === 'failed' || s === 'cancelled') { clearInterval(t); onError(d.job?.error ?? 'The render failed.'); }
      }).catch(() => undefined);
    }, LOOK_EVERY_MS);
    return () => { alive = false; clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rendering, cardId, stalled]);

  async function render() {
    if (!cardId) return;
    setBusy('render'); onError(null); setPlaying(false);
    try { const r = await api.render(cardId, { again: renderStatus === 'complete' }); setRenderStatus(r.reused ? 'complete' : 'queued'); if (r.reused) { setCut((await api.preview(cardId)).download.url); setShowCut(true); } }
    catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  }

  const bedKey = live?.card.mix?.music?.key ?? null;
  const bedId = bedKey ? assets.find((a) => a.media_key === bedKey)?.id ?? '' : '';
  const why = !live ? 'Pick a movie or make one'
    : live.rundown.length === 0 ? 'Add a clip to the strip'
    : dirty ? 'Save the strip first'
    : rendering ? 'Rendering…'
    : null;
  const readout = playhead !== null ? `${mmss(playhead)} / ${mmss(total)}` : `${live?.rundown.length ?? 0} ${live?.rundown.length === 1 ? 'scene' : 'scenes'} · ${mmss(total)}`;

  return (
    <div className="quick-tab">
      <div className="quick-actions" data-tool="quick-movie-pick">
        <select value={cardId ?? ''} onChange={(e) => setCardId(e.target.value || null)} aria-label="Movie">
          <option value="">Pick a movie…</option>
          {mine.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <input value={title} maxLength={200} placeholder="Or name a new one" onChange={(e) => setTitle(e.target.value)} />
        <button type="button" className="btn" disabled={busy !== null} onClick={() => void makeCard()}>New movie</button>
        <button type="button" className="btn tiny" onClick={() => onGo('start', 'idea-story')}>Start from an idea</button>
      </div>

      {live && cardId ? (
        showCut && cut ? (
          <Stage tool="quick-movie-cut">
            <video src={cut} controls autoPlay />
          </Stage>
        ) : (
          <div className="quick-stage-frame" data-tool="quick-movie-stage">
            <FramePreview cardId={cardId} line={selected} rundown={live.rundown} title={live.card.title} readout={readout} motion={motion} />
          </div>
        )
      ) : (
        <Stage empty="Pick a movie above, or name a new one. Then drop pictures and clips onto the strip." />
      )}

      {live && (
        <>
          <div className="quick-actions">
            <button type="button" className="btn" disabled={!live.rundown.length} onClick={togglePlay} data-tool="quick-movie-play">{playing ? 'Pause' : 'Play rough'}</button>
            {cut && <button type="button" className="btn tiny" onClick={() => setShowCut((v) => !v)}>{showCut ? 'Back to the strip' : 'Watch the cut'}</button>}
            <span className="sub-dim">A rough play: the frames the render draws, the footage under them. The bed and the joins are heard in the render.</span>
          </div>

          <div className="quick-strip quick-strip-movie" data-tool="quick-movie-strip"
               onDragOver={(e) => { if (isAssetDrag(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
               onDrop={(e) => { const a = readDrag(e); if (a) { e.preventDefault(); void add(a); } }}>
            {live.rundown.length === 0 && <p className="sub-dim quick-note">Drop pictures and clips here from the shelf below, or click one to add it.</p>}
            {live.rundown.map((t, i) => {
              const src = t.sourceId ? sources.find((s) => s.id === t.sourceId) : null;
              return (
                <div key={t.id ?? `n${i}`} className={`quick-strip-item quick-scene${selected === i ? ' on' : ''}${over === i ? ' over' : ''}`}
                     draggable onDragStart={() => setDragging(i)} onDragEnd={() => { setDragging(null); setOver(null); }}
                     onDragOver={(e) => { e.preventDefault(); setOver(i); if (isAssetDrag(e)) e.dataTransfer.dropEffect = 'copy'; }}
                     onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const a = readDrag(e); if (a) { void add(a, i); } else if (dragging !== null) move(dragging, i); setDragging(null); setOver(null); }}
                     onClick={() => { setSelected(i); setPlaying(false); setPlayhead(starts[i] ?? 0); }}
                     title={src ? src.name : t.text || `scene ${i + 1}`}>
                  <span className="quick-scene-n">{i + 1}</span>
                  <span className="quick-scene-name">{src ? src.name : t.kind === 'title' ? t.text : t.text ? t.text.slice(0, 40) : 'drawn scene'}</span>
                  <label className="quick-scene-hold">
                    <input type="number" min={1} max={600} step={1} value={secs[i]} onClick={(e) => e.stopPropagation()} onChange={(e) => hold(i, Number(e.target.value))} aria-label={`Seconds for scene ${i + 1}`} /> s
                  </label>
                  <button type="button" className="btn tiny" title="Take it off the strip. The file stays in the library." onClick={(e) => { e.stopPropagation(); drop(i); }}>×</button>
                </div>
              );
            })}
          </div>

          <div className="quick-actions">
            <label className="sub-dim">Bed
              <select value={bedId} disabled={busy !== null} onChange={(e) => void setBed(e.target.value)} aria-label="Bed" style={{ marginLeft: 6 }}>
                <option value="">none</option>
                {assets.filter((a) => a.kind === 'audio').map((a) => <option key={a.id} value={a.id}>{displayName(a)}</option>)}
              </select>
            </label>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn" disabled={!dirty || busy !== null} onClick={() => void save()} data-tool="quick-movie-save">{busy === 'save' ? 'Saving…' : dirty ? 'Save' : 'Saved'}</button>
            <SendButton label="Render" busyLabel={rendering ? `Rendering… ${renderStatus}` : 'Queuing…'} busy={busy === 'render' || rendering} credits={quote ? quote.credits : null} why={why} onClick={() => void render()} />
            <OpenInPro what="The Flight Deck: the script, the layers, every join, and this movie open on it." onClick={() => onOpenInPro({ bench: 'deck', move: 'preview-play', cardId })} />
          </div>
          {stalled && <p className="sub-dim quick-note">{GAVE_UP} <button type="button" className="btn tiny" onClick={() => setStalled(false)}>Look again</button></p>}
          <QuoteLine text={quote ? `${sayQuote(quote.credits, `a ${saySeconds(Math.round(total))} render (${minutes} ${minutes === 1 ? 'minute' : 'minutes'} billed)`, quote.balance)} Saved movies play rough for free.` : null} />
        </>
      )}

      <div className="quick-chips" data-tool="quick-movie-shelf">
        <span className="sub-dim">The shelf — drag onto the strip, or click to add</span>
        <div className="quick-strip">
          {assets.filter((a) => a.kind === 'image' || a.kind === 'video').slice(0, 24).map((a) => {
            const d: DragAsset = { id: a.id, kind: a.kind, name: displayName(a), seconds: a.duration_ms ? a.duration_ms / 1000 : null };
            return (
              <button key={a.id} type="button" className="quick-strip-item" draggable onDragStart={(e) => writeDrag(e, d)} title={displayName(a)} disabled={busy !== null}
                      onClick={() => void add(d)}>
                <Thumb asset={a} size={96} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
